import { DialogController, DialogService } from 'aurelia-dialog';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { autoinject, bindable, PLATFORM } from 'aurelia-framework';
import { NavigationInstruction, Next, Redirect, Router, RouterConfiguration } from 'aurelia-router';
import { Prompt } from 'elements/prompt';
import { Models } from 'models/core';
import { ErrorService } from 'services/error-service';
import { LoopService } from 'services/loop-service';
import { NetService } from 'services/net-service';
import { AnalysisLoopService } from 'services/analysis-loop-service';
import { ToastService } from 'services/toast-service';
import { AnalysisService } from 'services/analysis-service';
import { Role } from 'models/UserModels';

@autoinject
export class FormLoop {
  private analysisId: number;
  private loops: Array<Models.AnalysisLoop>;
  private loopNewFormVisible: boolean = false;
  private loopEditFormVisible: number = null;
  private minimumBreakingStrength: any;
  private nextTabIndex: number = null;
  private locked: boolean = false;
  private validationMessages: Array<string>;
  private analysis: Models.Analysis;

  private subscription1: Subscription;
  private subscription2: Subscription;
  private subscription3: Subscription;
  private subscription4: Subscription;

  rolesAllowedToUpdate: Role[] = ['AnalysisEngineer'];

  constructor(
    private dialogService: DialogService,
    private errorService: ErrorService,
    private eventAggregator: EventAggregator,
    private loopService: LoopService,
    private netService: NetService,
    private analysisService: AnalysisService,
    private analysisLoopService: AnalysisLoopService,
    private router: Router,
    private toastService: ToastService
  ) {}

  private activate(params) {
    this.analysisId = parseInt(params.Id);
    this.getLoops(this.analysisId);
    //this.getLoops(this.netId);

    // Update loops array when creating new loops
    this.subscription1 = this.eventAggregator.subscribe('loopListReset', (value) => {
      this.loopEditFormVisible = null;
      this.getLoops(this.analysisId);
    });

    this.subscription3 = this.eventAggregator.subscribe('loopFormEditClose', (value) => {
      return this.getLoops(this.analysisId);
    });

    this.eventAggregator.subscribe('loopFormNewClose', (value) => {
      this.loopNewFormVisible = false;
    });

    // Get new tabIndex for tabs component via EA, store value so we can publish this if canDeactivate returns true
    this.eventAggregator.subscribe('changeTab', (tabIndex) => {
      this.nextTabIndex = tabIndex;
    });
  }

  private deactivate() {
    if (this.subscription1) {
      this.subscription1.dispose();
    }
    if (this.subscription2) {
      this.subscription2.dispose();
    }
    if (this.subscription3) {
      this.subscription3.dispose();
    }
    if (this.subscription4) {
      this.subscription4.dispose();
    }
  }

  private canDeactivate() {
    if (this.loopNewFormVisible || this.loopEditFormVisible > 0) {
      return this.dialogService
        .open({
          viewModel: Prompt,
          model: {
            header: 'dialog.subFormOpenHeading',
            message: 'dialog.subFormOpenMessage',
            actions: {
              delete: { enabled: false },
              save: { enabled: false },
              cancel: { enabled: true, t: 'dialog.cancel' },
              dontsave: { enabled: false },
              continue: { enabled: true, t: 'dialog.continue' },
            },
          },
        })
        .whenClosed((response) => {
          if (response.wasCancelled) {
            return false;
          } else {
            const result = response.output;
            if (result === 'continue') {
              this.eventAggregator.publish('changeTab-success', this.nextTabIndex);
              return true;
            }
          }
        });
    } else {
      this.eventAggregator.publish('changeTab-success', this.nextTabIndex);
      return true;
    }
  }

  private getAnalysis() {
    this.analysisService
      .get(this.analysisId + '?$expand=NetStandard')
      .then((res) => {
        this.analysis = res;
        this.locked = res.Locked ? res.Locked : false;
      })
      .catch((err) => this.errorService.handleError(err));
  }

  private getLoops(analysisId) {
    this.analysisLoopService
      .getAll(
        '?$expand=LoopType,LoopPositionVertical,LoopPositionHorizontal,Coupling&$filter=AnalysisId eq ' + analysisId
      )
      .then((res) => {
        this.loops = res;
        this.getAnalysis();
      })
      .catch((err) => this.errorService.handleError(err));
  }

  private deleteLoop(id) {
    this.analysisLoopService
      .delete(id)
      .then((res) => {
        this.toastService.showSuccess('loop.deleted');
        this.getLoops(this.analysisId);
      })
      .catch((err) => this.errorService.handleError(err));
  }
}
